import { getMeetings } from '@/api/meetings';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Container, Divider, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { Plus as PlusIcon } from '@phosphor-icons/react/dist/ssr/Plus';
import { PresentationChart as PresentationChartIcon } from '@phosphor-icons/react/dist/ssr/PresentationChart';

import type { Meeting } from '@/types/meeting';
import { useDialog } from '@/hooks/v2/use-dialog';
import { Seo } from '@/components/seo';
import { UserContext } from '@/contexts/auth/user-context';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { ACCOUNT_ROLES } from '@/constants/account-roles';

import { MeetingsTable } from './components/meetings-table';
import CreateMeetingDialog from './dialogs/CreateMeetingDialog';
import React from 'react';

export default function MeetingsPage() {
  const dialog = useDialog();
  const userContext = React.useContext(UserContext);

  const { data: meetings = [], isLoading, error } = useQuery<Meeting[]>({
    queryKey: ['meetings'],
    queryFn: getMeetings,
  });

  const canCreateMeeting = React.useMemo(() => {
    if (!userContext?.user) return false;

    const accountRoleNames = (userContext.user.account_roles ?? []).map((r) => r.name);
    const communityRoleNames = (userContext.user.community_roles ?? []).map((r) => r.name);

    const isAccountAdmin = accountRoleNames.includes(ACCOUNT_ROLES.ACCOUNT_ADMIN) ||
      accountRoleNames.includes(ACCOUNT_ROLES.ACCOUNT_OWNER) ||
      accountRoleNames.includes(ACCOUNT_ROLES.ACCOUNT_MANAGER);

    const isBoardMember = communityRoleNames.includes(COMMUNITY_ROLES.BOARD_MEMBER);

    return isAccountAdmin || isBoardMember;
  }, [userContext?.user]);

  return (
    <>
      <Seo title="Meetings" />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>
        {/* ── Header strip ─────────────────────────────── */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>Meetings</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
                  View and manage meetings scheduled for your community
                </Typography>
              </Box>
              {canCreateMeeting ? (
                <Button
                  onClick={dialog.handleOpen}
                  size="small"
                  startIcon={<PlusIcon size={16} />}
                  variant="contained"
                >
                  New Meeting
                </Button>
              ) : null}
            </Stack>
          </Container>
        </Box>

        {/* ── Body ─────────────────────────────────────── */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Paper
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
          >
            {isLoading ? (
              <Stack>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Box key={i} sx={{ px: 3, py: 2 }}>
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Skeleton height={18} variant="text" width="55%" />
                        <Skeleton height={14} sx={{ mt: 0.5 }} variant="text" width="35%" />
                      </Box>
                      <Skeleton height={14} variant="text" width={80} />
                    </Stack>
                    {i < 5 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </Stack>
            ) : error ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography color="error" variant="body2">
                  Unable to load meetings. Please try again.
                </Typography>
              </Box>
            ) : meetings.length === 0 ? (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <PresentationChartIcon color="var(--mui-palette-text-disabled)" size={48} style={{ marginBottom: 12 }} />
                <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="body1">
                  No meetings yet
                </Typography>
                <Typography color="text.disabled" sx={{ mt: 0.5 }} variant="body2">
                  Create a meeting to get started
                </Typography>
              </Box>
            ) : (
              <MeetingsTable error={error} loading={isLoading} rows={meetings} />
            )}
          </Paper>
        </Container>
      </Box>

      {canCreateMeeting ? <CreateMeetingDialog onClose={dialog.handleClose} open={dialog.open} /> : null}
    </>
  );
}
